import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type {
  Conference,
  DeadlineDelinquency,
  ParticipantResult,
  SeasonStatus,
  StandingsRow,
  Streak,
  TiebreakerNote,
} from '../types'
import { TeamHelmet } from './TeamHelmet'
import { resolveTecmoTeam } from '../constants/teams'
import {
  defaultDirection,
  sortRows,
  type SortDirection,
  type SortKey,
} from '../lib/tableSort'
import './ui.css'

/** Small presentational primitives shared across pages. */

export function Card({
  title,
  subtitle,
  actions,
  conference,
  flush,
  children,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  conference?: Conference | null
  flush?: boolean
  children: ReactNode
}) {
  const accent =
    conference === 'AFC' ? ' card--afc' : conference === 'NFC' ? ' card--nfc' : ''

  return (
    <section className={`card${accent}`}>
      {(title || actions) && (
        <header className="card__header">
          <div>
            {title && <div className="card__title">{title}</div>}
            {subtitle && <div className="card__subtitle">{subtitle}</div>}
          </div>
          {actions}
        </header>
      )}
      <div className={flush ? 'card__body card__body--flush' : 'card__body'}>{children}</div>
    </section>
  )
}

/**
 * Labelled figure used across dashboard grids.
 *
 * @param size - `compact` shrinks and clips the value; use it whenever the
 * value is text such as a player name, which would otherwise wrap the tile to a
 * different height than its neighbours.
 * @param footer - Rendered below the hint, for extras like a progress bar.
 */
export function Stat({
  label,
  value,
  hint,
  size = 'default',
  footer,
  accent,
  to,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  size?: 'default' | 'compact'
  footer?: ReactNode
  /** Tints the label with a conference colour, red for AFC and blue for NFC. */
  accent?: Lowercase<Conference>
  /** Turns the whole tile into a link to this destination. */
  to?: string
}) {
  const valueClass = size === 'compact' ? 'stat__value stat__value--compact' : 'stat__value'
  const labelClass = accent ? `stat__label stat__label--${accent}` : 'stat__label'

  const body = (
    <>
      <div className={labelClass}>{label}</div>
      <div className={valueClass}>{value}</div>
      {hint && <div className="stat__hint">{hint}</div>}
      {footer}
    </>
  )

  if (to) {
    return (
      <Link className="stat stat--link" to={to}>
        {body}
      </Link>
    )
  }

  return <div className="stat">{body}</div>
}

/**
 * Thin brand-gradient progress track.
 *
 * @param value - Amount completed.
 * @param total - Target; the bar renders nothing when this is zero or unknown,
 * since an empty track claiming "0 of 0" is worse than no bar at all.
 * @param label - Readable counterpart to the bar, e.g. `2 of 66`.
 *
 * @example
 * ```tsx
 * <ProgressBar value={2} total={66} label="2 of 66 games played" />
 * ```
 */
export function ProgressBar({
  value,
  total,
  label,
}: {
  value: number
  total: number
  label?: ReactNode
}) {
  if (!Number.isFinite(total) || total <= 0) {
    return null
  }

  const percent = Math.min(100, Math.max(0, (value / total) * 100))

  return (
    <div className="meter">
      <div
        className="meter__track"
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div className="meter__fill" style={{ width: `${percent}%` }} />
      </div>
      {label && <div className="meter__label">{label}</div>}
    </div>
  )
}

/**
 * Hover and keyboard-focus hint anchored above its trigger.
 *
 * @param text - Rendered with preserved whitespace, so deliberate spacing in
 * the copy survives.
 */
export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  return (
    <span className="tooltip">
      {children}
      <span className="tooltip__bubble" role="tooltip">
        {text}
      </span>
    </span>
  )
}

/**
 * Discord avatar with an initials fallback.
 *
 * @param discordId - Needed to build the CDN URL; without it we show initials.
 */
export function Avatar({
  name,
  discordId,
  avatar,
  size = 'sm',
}: {
  name: string | null
  discordId?: string | null
  avatar?: string | null
  size?: 'sm' | 'lg'
}) {
  const className = size === 'lg' ? 'avatar avatar--lg' : 'avatar'
  const label = (name ?? '?').trim()
  const initials = label
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0] ?? '')
    .join('')
    .toUpperCase()

  if (discordId && avatar) {
    return (
      <img
        className={className}
        src={`https://cdn.discordapp.com/avatars/${discordId}/${avatar}.png?size=64`}
        alt=""
        loading="lazy"
      />
    )
  }

  return (
    <div className={className} aria-hidden="true">
      {initials || '?'}
    </div>
  )
}

/**
 * A player's avatar and name, optionally with the team they used.
 *
 * @param team - Abbreviation of the team played in this game. Standings rows
 * omit it, since a player uses a different team every week.
 * @param to - Turns the name into a link, in practice always
 * {@link playerProfilePath}. Omit it anywhere this cell is already inside an
 * anchor — the recent-results list on the home page wraps the whole row in one
 * — because a nested anchor is invalid markup and browsers recover from it by
 * splitting the element out of its parent. A cell inside a {@link RowLink} is
 * *not* such a place: that row is a `<tr>`, not an anchor.
 * @param flag - Marks the name in the brand red and carries this sentence as
 * its tooltip. The sentence is not optional alongside the colour: a reader who
 * cannot separate red from grey, or who is being read the page aloud, gets
 * nothing at all from the tint on its own.
 */
export function PlayerCell({
  name,
  discordId,
  avatar,
  team,
  to,
  flag,
}: {
  name: string | null
  discordId?: string | null
  avatar?: string | null
  team?: string | null
  to?: string
  flag?: string | null
}) {
  const nameClass = flag ? 'player__name player__name--flagged' : 'player__name'

  const resolvedTeam = resolveTecmoTeam(team)

  return (
    <div className="player">
      <Avatar name={name} discordId={discordId} avatar={avatar} />
      {to ? (
        <Link className={`${nameClass} player__name--link`} to={to} title={flag ?? undefined}>
          {name ?? 'Unassigned'}
        </Link>
      ) : (
        <span className={nameClass} title={flag ?? undefined}>
          {name ?? 'Unassigned'}
        </span>
      )}
      {resolvedTeam && (
        <span className="player__team">
          <TeamHelmet abbreviation={resolvedTeam.abbreviation} size="sm" />
          <span className="team-tag">{resolvedTeam.abbreviation}</span>
        </span>
      )}
    </div>
  )
}

/**
 * The games list narrowed to one player's results in one season.
 *
 * @param leaguePlayerId - Roster id, which every standings row carries. Note
 * that `StandingsRow.userId` holds this, not the Discord-linked account id, so
 * the three players who have never signed in still address correctly.
 * @param seasonNumber - Season being viewed, or `'all'` for a player's whole
 * career. Omit it only while the season is still loading; the games list then
 * falls back to the current season, which is what a bare `/games?player=…` has
 * always meant — so omitting it is *not* the way to ask for every game.
 *
 * @example
 * ```typescript
 * playerSeasonGamesPath(7, 32)    // '/games?player=7&season=32'
 * playerSeasonGamesPath(7, 'all') // '/games?player=7&season=all'
 * ```
 */
export function playerSeasonGamesPath(
  leaguePlayerId: number,
  seasonNumber?: number | 'all'
): string {
  const params = new URLSearchParams({ player: String(leaguePlayerId) })
  if (seasonNumber !== undefined) {
    params.set('season', String(seasonNumber))
  }
  return `/games?${params}`
}

/**
 * The games list narrowed to one rivalry's meetings.
 *
 * @param leaguePlayerId - Whose profile the rivalry is being read from.
 * @param opponentLeaguePlayerId - `HeadToHeadRow.opponentUserId`.
 *
 * @remarks
 * Always every season. A rivalry record is a career figure, so scoping the
 * meetings behind it to one season would show a list that cannot add up to the
 * row the visitor just clicked.
 *
 * @example
 * ```typescript
 * headToHeadGamesPath(7, 12) // '/games?player=7&opponent=12&season=all'
 * ```
 */
export function headToHeadGamesPath(
  leaguePlayerId: number,
  opponentLeaguePlayerId: number
): string {
  const params = new URLSearchParams({
    player: String(leaguePlayerId),
    opponent: String(opponentLeaguePlayerId),
    season: 'all',
  })
  return `/games?${params}`
}

/**
 * A player's career page.
 *
 * @param leaguePlayerId - Roster id. `StandingsRow.userId`,
 * `CoachStats.leaguePlayerId`, `GameSide.leaguePlayerId` and
 * `HeadToHeadRow.opponentUserId` all hold this one key, which is what lets
 * every player name on the site address the same page.
 *
 * @example
 * ```typescript
 * playerProfilePath(7) // '/players/7'
 * ```
 */
export function playerProfilePath(leaguePlayerId: number): string {
  return `/players/${leaguePlayerId}`
}

/**
 * One in-game player's game log — the NES athlete, not a league member.
 *
 * @param teamAbbreviation - `RosterPlayerStats.teamAbbreviation`. Not optional:
 * rosters are team-specific, so it is half the identity rather than a filter,
 * and a log addressed by name alone could pool two different athletes.
 * @param name - `RosterPlayerStats.name`.
 * @param seasonNumber - Season being viewed, or `'all'` for every season. Omit
 * it only while the season is still loading; the log then falls back to the
 * current season, exactly as the games list does.
 *
 * @remarks
 * Query parameters rather than path segments because roster names carry spaces
 * and periods — `J.J. Birden`, `Ken O.Brien` — and one of them is even a bare
 * slot code like `WR2` for a team the roster table does not know. `URLSearchParams`
 * escapes all of that; a path segment would need its own encode-and-decode pair
 * on both ends and would silently mangle the first name containing a slash.
 *
 * @example
 * ```typescript
 * inGamePlayerLogPath('ATL', 'Andre Rison', 33)
 * // '/in-game-players?team=ATL&name=Andre+Rison&season=33'
 * ```
 */
export function inGamePlayerLogPath(
  teamAbbreviation: string,
  name: string,
  seasonNumber?: number | 'all'
): string {
  const params = new URLSearchParams({ team: teamAbbreviation, name })
  if (seasonNumber !== undefined) {
    params.set('season', String(seasonNumber))
  }
  return `/in-game-players?${params}`
}

/** How far a pointer may travel between press and release and still count as a click. */
const ROW_DRAG_SLOP_PX = 8

/**
 * A table row whose whole surface activates one destination.
 *
 * @param to - Where a click in the row goes, or null to render an ordinary
 * inert row.
 *
 * @remarks
 * This is a mouse convenience layered on top of a real link, never the only way
 * in: the row is deliberately not focusable and carries no `role`, because a
 * focusable row would announce ten cells of numbers as one link label. Every
 * caller must therefore put a genuine anchor to the same `to` inside the row —
 * {@link RowLinkTarget} exists for exactly that — which is what keyboard and
 * screen-reader users actually traverse.
 *
 * That anchor used to be the player's name, but a name now links to that
 * player's profile, which is a *different* destination from the row's. Both
 * live in the row as siblings; neither is nested in the other, because the row
 * is a `<tr>` rather than an anchor and nesting one anchor in another is
 * invalid markup that browsers recover from by splitting the elements apart.
 *
 * Four kinds of click are deliberately *not* navigations:
 * - one that landed on a nested link or button, which owns the click itself and
 *   would otherwise navigate twice;
 * - one carrying a modifier key, which belongs to the anchor so open-in-new-tab
 *   and open-in-new-window keep working;
 * - one that ended a drag, which covers both selecting a score with the mouse
 *   and the sideways swipe the tables need inside `.table-wrap` on a phone;
 * - one that ended with text selected, catching a drag that started outside the
 *   row.
 */
export function RowLink({
  to,
  children,
}: {
  to: string | null
  children: ReactNode
}) {
  const navigate = useNavigate()
  const pressOrigin = useRef<{ x: number; y: number } | null>(null)

  if (!to) {
    return <tr>{children}</tr>
  }

  return (
    <tr
      className="row-link"
      onPointerDown={event => {
        pressOrigin.current = { x: event.clientX, y: event.clientY }
      }}
      onClick={event => {
        const origin = pressOrigin.current
        pressOrigin.current = null

        if (event.defaultPrevented) return
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        if (
          event.target instanceof Element &&
          event.target.closest('a, button, input, select, textarea, [role="button"]')
        ) {
          return
        }
        if (
          origin &&
          Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > ROW_DRAG_SLOP_PX
        ) {
          return
        }
        if (window.getSelection()?.toString()) return

        navigate(to)
      }}
    >
      {children}
    </tr>
  )
}

/**
 * The in-row anchor that carries a {@link RowLink}'s destination.
 *
 * @param to - The row's own `to`; null renders the content as inert text, for
 * a row that has nowhere to go.
 * @param label - Accessible name for the link. Say where it goes and for whom
 * — the visible content is usually a bare figure such as `8-2`, which means
 * nothing when a screen reader lists it among the page's other links.
 *
 * @remarks
 * A `RowLink` is mouse-only by design, so without this the row's destination is
 * unreachable by keyboard. It became a component of its own once the player's
 * name in a standings row started pointing at a profile instead: the row and
 * the name now go to two different places, and each needs its own anchor.
 *
 * Which cell to wrap is a judgement call — pick one whose content already
 * describes the destination. Standings wrap the record, because `8-2` is
 * exactly what the season's results add up to.
 *
 * @example
 * ```tsx
 * <RowLink to={gamesPath}>
 *   <td><PlayerCell name={row.displayName} to={playerProfilePath(row.userId)} /></td>
 *   <td className="record">
 *     <RowLinkTarget to={gamesPath} label={`${row.displayName} — season games`}>
 *       {formatRecord(row.wins, row.losses, row.ties)}
 *     </RowLinkTarget>
 *   </td>
 * </RowLink>
 * ```
 */
export function RowLinkTarget({
  to,
  label,
  children,
}: {
  to: string | null
  label: string
  children: ReactNode
}) {
  if (!to) {
    return <>{children}</>
  }

  return (
    <Link className="row-link__target" to={to} aria-label={label}>
      {children}
    </Link>
  )
}

/* ------------------------------------------------------- column sorting -- */

/**
 * The comparison rules live in `lib/tableSort` and are re-exported here so a
 * page only ever imports table pieces from one place.
 */
export {
  dateSortValue,
  recordSortKey,
  streakSortValue,
  type SortDirection,
  type SortKey,
  type SortValue,
} from '../lib/tableSort'

/**
 * One column of a sortable table.
 *
 * @param key - Stable identifier for this column; only needs to be unique
 * within its own table.
 * @param sort - Value to order by. Omit it to render an inert header, which is
 * what a rank counter or a button column wants.
 * @param firstDirection - Direction a fresh click applies. Defaults to
 * descending for numeric columns and ascending for text, so one click on a
 * statistic puts the best row on top while one click on a name reads A–Z.
 * @param hint - Tooltip on the header button. Worth setting wherever the
 * rendered column is not literally the thing being compared, e.g. a `Score`
 * column that orders by combined points.
 */
export interface SortableColumn<Row> {
  key: string
  label?: ReactNode
  className?: string
  sort?: (row: Row) => SortKey
  firstDirection?: SortDirection
  hint?: string
  /** Plain-text column name for prose such as "Sorted by Points For". */
  sortLabel?: string
}

export interface SortState {
  key: string
  direction: SortDirection
}

/** Sorted rows plus the state and callbacks the headers need. */
export interface TableSort<Row> {
  rows: Row[]
  state: SortState | null
  /**
   * Whether the rows are still in the order the caller supplied.
   *
   * @remarks
   * Gate anything that describes a row's *position* on this — seed numbers,
   * the playoff cut, tiebreaker markers. Those claims are only true of the
   * server's ordering and become false the moment a visitor re-sorts.
   */
  isNatural: boolean
  /** Name of the active column, for a "sorted by …" note. Null when natural. */
  activeLabel: string | null
  toggle: (key: string) => void
  /** Returns the table to the order it loaded in. */
  reset: () => void
}

/**
 * Adds click-to-sort to a table the caller still renders itself.
 *
 * @param rows - Source rows in their natural order, straight from the API.
 * @param columns - Column definitions; stabilise this with `useMemo` so the
 * sort is not recomputed on every render.
 * @param initial - Ordering to load with, and the ordering a third click
 * returns to. Omit it for a table whose natural order is already meaningful.
 *
 * @remarks
 * The click cycle is three-state: first direction, reverse, then back to
 * {@link initial}. The third step matters more than it looks — it is the
 * escape hatch that lets a visitor recover the server's ordering, which for
 * standings is the only ordering the seeds and the playoff line describe.
 *
 * @example
 * ```tsx
 * const columns = useMemo<Array<SortableColumn<CareerRow>>>(() => [
 *   { key: 'rank', label: '#', className: 'rank' },
 *   { key: 'player', label: 'Player', sort: row => row.displayName },
 *   { key: 'diff', label: 'Diff', className: 'num', sort: row => row.pointDifferential },
 * ], [])
 *
 * const sort = useTableSort(rows, columns)
 * // <thead><SortableHeaderRow columns={columns} sort={sort} /></thead>
 * // then map over sort.rows
 * ```
 */
export function useTableSort<Row>(
  rows: readonly Row[],
  columns: ReadonlyArray<SortableColumn<Row>>,
  initial?: SortState | null
): TableSort<Row> {
  const [state, setState] = useState<SortState | null>(initial ?? null)

  const column = state ? columns.find(entry => entry.key === state.key) : undefined
  const accessor = column?.sort

  const sorted = useMemo(
    () => (accessor && state ? sortRows(rows, accessor, state.direction) : [...rows]),
    [rows, accessor, state]
  )

  // Read through refs so a caller that rebuilds its column array — or passes a
  // fresh `initial` object — every render does not invalidate the callbacks.
  const latest = useRef({ columns, rows, initial })
  latest.current = { columns, rows, initial }

  const toggle = useCallback((key: string) => {
    const { columns: current, rows: currentRows, initial: currentInitial } = latest.current
    const target = current.find(entry => entry.key === key)
    if (!target?.sort) return

    const first = target.firstDirection ?? defaultDirection(currentRows, target.sort)

    setState(previous => {
      if (!previous || previous.key !== key) return { key, direction: first }
      if (previous.direction === first) {
        return { key, direction: first === 'asc' ? 'desc' : 'asc' }
      }
      return currentInitial ?? null
    })
  }, [])

  const reset = useCallback(() => setState(latest.current.initial ?? null), [])

  return {
    rows: sorted,
    state: accessor ? state : null,
    isNatural: !accessor || state === null,
    activeLabel: accessor
      ? (column?.sortLabel ??
        (typeof column?.label === 'string' ? column.label : (column?.key ?? null)))
      : null,
    toggle,
    reset,
  }
}

const ARIA_SORT: Record<SortDirection, 'ascending' | 'descending'> = {
  asc: 'ascending',
  desc: 'descending',
}

/**
 * One table heading, sortable when its column carries a `sort` accessor.
 *
 * @remarks
 * The control is a real `<button>` inside the `<th>` rather than a handler on
 * the cell, so it is reachable by Tab, activates on Enter and Space for free,
 * and is announced as a control instead of as static text. `aria-sort` lives on
 * the `<th>`, which is where assistive technology looks for it, and is set to
 * `none` on the other sortable columns so their inert neighbours stay
 * distinguishable from an unsorted-but-sortable one.
 */
export function SortableHeader<Row>({
  column,
  sort,
}: {
  column: SortableColumn<Row>
  sort: TableSort<Row>
}) {
  const active = sort.state?.key === column.key
  const direction = active ? (sort.state as SortState).direction : null

  if (!column.sort) {
    return <th className={column.className}>{column.label}</th>
  }

  const className = [column.className, 'is-sortable'].filter(Boolean).join(' ')

  return (
    <th className={className} aria-sort={direction ? ARIA_SORT[direction] : 'none'}>
      <button
        type="button"
        className={active ? 'th-sort th-sort--active' : 'th-sort'}
        title={column.hint}
        onClick={() => sort.toggle(column.key)}
      >
        <span>{column.label}</span>
        <span className="th-sort__arrow" aria-hidden="true">
          {direction === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  )
}

/** The whole heading row of a sortable table. */
export function SortableHeaderRow<Row>({
  columns,
  sort,
}: {
  columns: ReadonlyArray<SortableColumn<Row>>
  sort: TableSort<Row>
}) {
  return (
    <tr>
      {columns.map(column => (
        <SortableHeader key={column.key} column={column} sort={sort} />
      ))}
    </tr>
  )
}

/**
 * Number of players per conference that qualify for the postseason.
 *
 * @remarks
 * From the Rules sheet: "6 teams will make playoffs from each conf. Top 2 each
 * conf will get byes." Both cuts are drawn on the seeded conference order, which
 * the server produces with the league's tiebreakers applied.
 */
export const PLAYOFF_SEED_COUNT = 6

/** Number of players per conference that skip the first postseason round. */
export const BYE_SEED_COUNT = 2

/**
 * Whether the postseason cut belongs after the given row.
 *
 * @remarks
 * Suppressed when the field is not deeper than the cut, since a line drawn
 * below the last player would imply an elimination that cannot happen.
 */
export function isPlayoffCutAfter(index: number, rowCount: number): boolean {
  return index === PLAYOFF_SEED_COUNT - 1 && rowCount > PLAYOFF_SEED_COUNT
}

/** Whether the first-round bye cut belongs after the given row. */
export function isByeCutAfter(index: number, rowCount: number): boolean {
  return index === BYE_SEED_COUNT - 1 && rowCount > BYE_SEED_COUNT
}

/**
 * The seeding divider that belongs after one standings row, if any.
 *
 * @param index - Zero-based position of the row just rendered.
 * @param rowCount - Size of the field, so a cut below the last player is
 * suppressed rather than left dangling.
 * @param columnCount - Columns to span; must match the table's header count or
 * the row will not stretch the full width.
 *
 * @remarks
 * One component for both cuts so the two standings tables cannot drift apart on
 * where a line goes. Render it unconditionally after every row of a *seeded*
 * conference and let it decide; it returns null everywhere else. Never render it
 * against the league-wide table, which is a record ranking rather than a seeding
 * and has no postseason meaning.
 *
 * The variants are deliberately unequal in weight. The playoff cut is the
 * consequential one and keeps the tinted band and brand-coloured rule; the bye
 * cut is a hairline, because two equally loud lines in a twelve-row table make
 * the one that decides seasons harder to find.
 *
 * @example
 * ```tsx
 * <Fragment key={row.userId}>
 *   <tr>{cells}</tr>
 *   {seeded && <SeedCutRow index={index} rowCount={rows.length} columnCount={9} />}
 * </Fragment>
 * ```
 */
export function SeedCutRow({
  index,
  rowCount,
  columnCount,
}: {
  index: number
  rowCount: number
  columnCount: number
}) {
  const cut = isPlayoffCutAfter(index, rowCount)
    ? { variant: 'playoff', label: `Playoff line · top ${PLAYOFF_SEED_COUNT} advance` }
    : isByeCutAfter(index, rowCount)
      ? { variant: 'bye', label: `First-round bye · top ${BYE_SEED_COUNT}` }
      : null

  if (!cut) return null

  return (
    <tr className={`seed-cut seed-cut--${cut.variant}`}>
      <td colSpan={columnCount}>
        <span className="seed-cut__label">{cut.label}</span>
      </td>
    </tr>
  )
}

/** The league's seeding order, worded for a tooltip. */
const TIEBREAKER_RULES =
  'Two players tied: head-to-head winner seeds higher.\n' +
  'Three or more tied: the player who beat every other tied player seeds first; otherwise the best point differential is pulled out and the rest start over.\n' +
  'Once two remain, it is head-to-head again — even if their differentials differ.'

/**
 * Marks a standings row whose seed a tiebreaker decided.
 *
 * @param note - Null for a row that earned its spot outright, which renders
 * nothing at all — the marker has to stay rare enough to mean something.
 *
 * @remarks
 * Carries a native `title` rather than a {@link Tooltip}: standings tables live
 * inside a horizontally scrolling wrapper, which clips an absolutely positioned
 * bubble on the first and last rows. The rule list in
 * {@link TiebreakerFootnote} sits outside that wrapper and does use `Tooltip`.
 *
 * @example
 * ```tsx
 * <td className="record">
 *   {formatRecord(row.wins, row.losses, row.ties)}
 *   <TiebreakerFlag note={row.tiebreaker} />
 * </td>
 * ```
 */
export function TiebreakerFlag({ note }: { note: TiebreakerNote | null }) {
  if (!note) return null

  return (
    <span className="tiebreak-flag" title={note.detail}>
      †
    </span>
  )
}

/**
 * Banner saying a table has been re-sorted, with the way back.
 *
 * @param label - Name of the column currently driving the order.
 * @param onReset - Restores the natural order.
 * @param resetLabel - Wording for the button; name the order being returned
 * to ("Back to seed order") rather than the generic "Clear sort", so the
 * visitor knows what they get.
 * @param children - Optional extra sentence naming what the re-sort hid.
 *
 * @remarks
 * Renders above the table rather than below it, because on a standings table
 * this note is the reason the rank numbers and the playoff line disappeared and
 * it is useless if you have to scroll past thirteen rows to find it.
 */
export function SortedAwayNote({
  label,
  onReset,
  resetLabel,
  children,
}: {
  label: string
  onReset: () => void
  resetLabel: string
  children?: ReactNode
}) {
  return (
    <div className="table-note table-note--top">
      <span>
        Sorted by <strong>{label}</strong>. {children}
      </span>
      <button type="button" className="btn btn--ghost btn--sm" onClick={onReset}>
        {resetLabel}
      </button>
    </div>
  )
}

/**
 * Explains the dagger under a conference table.
 *
 * @param rows - The rendered conference, so the note can stay hidden until at
 * least one seed actually turned on a tiebreaker.
 *
 * @remarks
 * Only pass conference rows. The combined overall table is a plain record
 * ranking, and a footnote there would claim a seeding it does not perform.
 */
export function TiebreakerFootnote({ rows }: { rows: StandingsRow[] }) {
  if (!rows.some(row => row.tiebreaker)) return null

  return (
    <p className="table-note">
      † Seed decided by a{' '}
      <Tooltip text={TIEBREAKER_RULES}>
        <span className="table-note__term">tiebreaker</span>
      </Tooltip>{' '}
      — hover a marker for the reason.
    </p>
  )
}

/**
 * Why a player's name is red, worded for a tooltip.
 *
 * @param delinquency - Null for a player who met every deadline, which renders
 * no marking at all.
 *
 * @example
 * ```typescript
 * describeDelinquency({ missedMidseason: true, missedEndOfSeason: false })
 * // 'Missed the midseason deadline — loses every tiebreaker.'
 * ```
 */
export function describeDelinquency(delinquency: DeadlineDelinquency | null): string | null {
  if (!delinquency) return null

  const missed =
    delinquency.missedMidseason && delinquency.missedEndOfSeason
      ? 'the midseason and end-of-season deadlines'
      : delinquency.missedMidseason
        ? 'the midseason deadline'
        : 'the end-of-season deadline'

  return `Missed ${missed} — loses every tiebreaker.`
}

/**
 * Explains a red name under a standings table.
 *
 * @param rows - The table as rendered, so the note stays hidden until somebody
 * in view has actually missed a deadline. A standing legend for a condition
 * nobody has hit is noise, and every season before 33 sets no deadlines at all.
 *
 * @remarks
 * Belongs with any table that renders {@link StandingsRow} names, not only the
 * seeded ones: the tint follows the player rather than their position, so it
 * shows up in the overall table and the home previews too.
 */
export function DelinquencyNote({ rows }: { rows: StandingsRow[] }) {
  if (!rows.some(row => row.delinquency)) return null

  return (
    <div className="table-note">
      <Notice>
        {/* One element, because a Notice lays its children out as flex items and
            a bare span would break onto a column of its own. */}
        <span>
          A <span className="player__name--flagged">name in red</span> missed one of the
          season&rsquo;s deadlines — either too few games played by the midseason date, or
          a round robin still unfinished at the end-of-season date. Missing either costs
          that player every tiebreaker, so they seed below anyone they are tied with.
          Hover a red name for which deadline.
        </span>
      </Notice>
    </div>
  )
}

export function ConferenceBadge({ conference }: { conference: Conference | null }) {
  if (!conference) {
    return <span className="badge">Unassigned</span>
  }
  return (
    <span className={`badge badge--${conference.toLowerCase()}`}>{conference}</span>
  )
}

const SEASON_STATUS_LABELS: Record<SeasonStatus, string> = {
  preseason: 'Preseason',
  active: 'Active',
  postseason: 'Postseason',
  complete: 'Complete',
}

/** Every lifecycle stage in progression order, for pickers and toggles. */
export const SEASON_STATUSES: SeasonStatus[] = [
  'preseason',
  'active',
  'postseason',
  'complete',
]

/**
 * True when new games in this season are stored as playoff games.
 *
 * @param status - The season's current lifecycle stage.
 */
export function seasonRecordsPlayoffs(status: SeasonStatus): boolean {
  return status === 'postseason'
}

/**
 * Display label for a season's lifecycle stage.
 *
 * @example
 * ```typescript
 * formatSeasonStatus('preseason') // 'Preseason'
 * ```
 */
export function formatSeasonStatus(status: SeasonStatus): string {
  return SEASON_STATUS_LABELS[status]
}

/**
 * Marks a game as postseason.
 *
 * @param isPlayoff - Straight off `GameSummary.isPlayoff`; renders nothing when
 * false, so callers can drop it into a row unconditionally.
 * @param compact - Abbreviates to `PO` for a table cell with no room. The full
 * word stays in the tooltip either way.
 *
 * @remarks
 * The same badge in every list that renders a game, because a playoff result
 * read as a regular-season one is misleading everywhere — a loss that cost
 * somebody a title looks like an ordinary conference defeat without it. It is
 * also the only visible sign that a game is outside the standings, which
 * exclude the postseason.
 *
 * @example
 * ```tsx
 * <PlayoffBadge isPlayoff={game.isPlayoff} compact />
 * ```
 */
export function PlayoffBadge({
  isPlayoff,
  compact = false,
}: {
  isPlayoff: boolean
  compact?: boolean
}) {
  if (!isPlayoff) return null

  return (
    <span className="badge badge--playoff" title="Playoff game — not counted in standings">
      {compact ? 'PO' : 'Playoff'}
    </span>
  )
}

export function ResultBadge({ result }: { result: ParticipantResult | null }) {
  if (!result) return null
  if (result === 'tie') return <span className="badge">Tie</span>
  return (
    <span className={`badge badge--${result}`}>{result === 'win' ? 'Win' : 'Loss'}</span>
  )
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'error' | 'success'
  children: ReactNode
}) {
  return <div className={`notice notice--${tone}`}>{children}</div>
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      {children && <div>{children}</div>}
    </div>
  )
}

export function Loading({ rows = 5 }: { rows?: number }) {
  return (
    <div className="stack-sm" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="skeleton" style={{ height: 34 }} />
      ))}
    </div>
  )
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string; className?: string }>
  value: T
  onChange: (next: T) => void
}) {
  return (
    <div className="segmented" role="group">
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          className={option.className}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Formats a record as `W-L`, widening to `W-L-T` only when ties exist.
 *
 * @remarks
 * RTL games always produce a winner, so the third segment never appears for
 * league play. It stays conditional rather than being dropped outright because
 * a spreadsheet import could still carry a tie from an earlier era, and
 * printing that row as a plain `W-L` would understate the games played.
 *
 * @example
 * ```typescript
 * formatRecord(9, 3, 0) // '9-3'
 * formatRecord(9, 2, 1) // '9-2-1'
 * ```
 */
export function formatRecord(wins: number, losses: number, ties: number): string {
  return ties > 0 ? `${wins}-${losses}-${ties}` : `${wins}-${losses}`
}

/**
 * Formats a result run as a prefixed count.
 *
 * @param streak - Null for a player with no official games.
 * @returns `W3` or `L5`, or an em dash when there is no run.
 *
 * @example
 * ```typescript
 * formatStreak({ result: 'loss', length: 5 }) // 'L5'
 * formatStreak(null) // '—'
 * ```
 */
export function formatStreak(streak: Streak | null): string {
  return streak ? `${streak.result === 'win' ? 'W' : 'L'}${streak.length}` : '—'
}

/** Table cell rendering of {@link formatStreak}, tinted by outcome. */
export function StreakCell({ streak }: { streak: Streak | null }) {
  const variant = streak ? streak.result : 'none'
  return <span className={`streak streak--${variant}`}>{formatStreak(streak)}</span>
}

/** Renders a win percentage as a leading-dot figure, e.g. `.750`. */
export function formatPct(value: number): string {
  return value.toFixed(3).replace(/^0/, '')
}

/**
 * Renders a share of games as a whole percentage, e.g. `63%`.
 *
 * @param share - A fraction between 0 and 1, or null when there is nothing to
 * take a share of.
 *
 * @remarks
 * Deliberately not {@link formatPct}. A leading-dot `.625` is the convention for
 * a *winning* percentage and reads as an achievement; how often a player sat in
 * the P1 seat is neither, so it gets an explicit `%` that cannot be mistaken for
 * a record.
 *
 * Rounded to whole points to stay narrow in an already wide standings table.
 *
 * @example
 * ```typescript
 * formatSharePct(0.625) // '63%'
 * formatSharePct(null)  // '—'
 * ```
 */
export function formatSharePct(share: number | null): string {
  return share === null ? '—' : `${Math.round(share * 100)}%`
}

export function formatSigned(value: number): string {
  return value > 0 ? `+${value}` : String(value)
}

export function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Posted date, plus an edited date when the post has been changed.
 *
 * @example
 * ```typescript
 * formatPostedDate('2026-10-13', null)              // 'Oct 13, 2026'
 * formatPostedDate('2026-10-13', '2026-10-14')      // 'Oct 13, 2026 · Edited Oct 14, 2026'
 * ```
 */
export function formatPostedDate(postedAt: string, editedAt: string | null): string {
  const posted = formatDate(postedAt)
  return editedAt ? `${posted} · Edited ${formatDate(editedAt)}` : posted
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * Formats a `YYYY-MM-DD` calendar date without applying a timezone.
 *
 * @param value - Date-only string, or null for an unrecorded date.
 * @returns A display date, or an em dash when there is nothing to show.
 *
 * @remarks
 * {@link formatDate} is for timestamps. Passing a date-only string through it
 * parses as UTC midnight and then renders in the local zone, which shows the
 * previous day anywhere west of Greenwich.
 *
 * @example
 * ```typescript
 * formatCalendarDate('2026-08-16') // 'Aug 16, 2026'
 * formatCalendarDate(null) // '—'
 * ```
 */
export function formatCalendarDate(value: string | null): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '')
  if (!match) return '—'

  const [, year, month, day] = match
  return `${MONTH_NAMES[Number(month) - 1]} ${Number(day)}, ${year}`
}
